
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { CountryItem, AddCountryFormData } from './types';
import { useRegions } from '@/hooks/useRegions';
import { Plus } from 'lucide-react';

interface AddCountryFormProps {
  onCountryAdded: (country: CountryItem) => void;
  isMobile?: boolean;
}

export const AddCountryForm: React.FC<AddCountryFormProps> = ({ onCountryAdded, isMobile }) => {
  const [formData, setFormData] = useState<AddCountryFormData>({
    name: '',
    code: '',
    region_id: undefined,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { regions } = useRegions();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.code.trim()) {
      toast.error('Country name and code are required');
      return;
    }

    if (formData.code.length !== 2) {
      toast.error('Country code must be exactly 2 characters');
      return;
    }

    setIsSubmitting(true);

    try {
      const countryData = {
        name: formData.name.trim(),
        code: formData.code.toUpperCase().trim(),
        region_id: formData.region_id || null,
        active: true,
      };

      const { data, error } = await supabase
        .from('countries')
        .insert(countryData)
        .select(`
          *,
          regions (
            id,
            name,
            code
          )
        `)
        .single();

      if (error) {
        if (error.code === '23505') {
          toast.error('A country with this code already exists');
        } else {
          throw error;
        }
        return;
      }

      const newCountry: CountryItem = {
        ...data,
        id: data.code,
        social_media: [],
        featured_video: null,
        region_name: data.regions?.name || null,
        region_code: data.regions?.code || null,
      };

      onCountryAdded(newCountry);
      
      // Reset form
      setFormData({
        name: '',
        code: '',
        region_id: undefined,
      });
      
      toast.success(`${data.name} added successfully`);
    } catch (error) {
      console.error('Error adding country:', error);
      toast.error('Failed to add country');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plus size={20} />
          Add New Country
        </CardTitle>
        <CardDescription>
          Add a new country to the platform
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-3'}`}>
            <div className="space-y-2">
              <Label htmlFor="country-name">Country Name</Label>
              <Input
                id="country-name"
                type="text"
                placeholder="Enter country name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="country-code">Country Code</Label>
              <Input
                id="country-code"
                type="text"
                placeholder="e.g., US, CA, GB"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                maxLength={2}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="region">Region (Optional)</Label>
              <Select 
                value={formData.region_id || "none"} 
                onValueChange={(value) => setFormData({ ...formData, region_id: value === "none" ? undefined : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Region</SelectItem>
                  {regions
                    .filter(region => region.active)
                    .map((region) => (
                      <SelectItem key={region.id} value={region.id}>
                        {region.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Adding...' : 'Add Country'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
